<script lang="ts" setup>
  import { ref, computed } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';

  interface Props {
    ariaControls?: string;
    scrollable?: boolean;
    enable?: boolean;
  }

  const props = defineProps<Props>();
  const breakpoints = useBreakpoints();
  const open = ref(false);

  const specific = computed(() => ({
    'aria-expanded': open.value,
    'aria-controls': props.ariaControls,
    'role': 'button',
    'tabindex': '0',
  }));

</script>
<template>
  <div class="collapsible" :class="{'collapsible--scrollable': scrollable, 'collapsible--enable': enable}">
    <div
      class="collapsible__header-container"
      @click.capture="open = !open"
      @keydown.space.prevent="open = !open"
      @keydown.enter.prevent="open = !open"
      v-bind="(!breakpoints.lg || enable) ? {...specific} : {}"
    >
      <div class="collapsible__header">
        <slot name="header" />
      </div>
      <SfIconChevronDown class="collapsible__icon" :class="{'collapsible__icon--open': open}" aria-hidden="true" />
    </div>
    <div :id="ariaControls" class="collapsible__content" :class="{'collapsible__content--open': open}" :inert="!open && !breakpoints.lg">
      <div class="collapsible__content__inner">
        <slot name="content" />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  @use '@storefront/styles/mixins/media';

  :root {
    --sf-collapsible-gap: 0rem;
    --sf-collapsible-border: none;
    --sf-collapsible-border-bottom: 1px solid var(--secondary-border);
    --sf-collapsible-padding: 0;

    --sf-collapsible-header-padding: 0;
    --sf-collapsible-header-font-weight: var(--font-weight-semi);

    --sf-collapsible-icon-padding-lg: 0.5rem;
    --sf-collapsible-icon-height-lg: 2rem;
    --sf-collapsible-icon-width-lg: 2rem;

    --sf-collapsible-content-padding: 0;
    --sf-collapsible-content-padding-inner: 0;

    --sf-collapsible-scrollable-content-padding-inner: 0 1rem 0 0;

    --sf-collapsible-input-background: var(--sf-input-background);

    @include media.query(lg) {
      --sf-collapsible-gap: 0;

      --sf-collapsible-header-padding: 0.75rem 0;

      --sf-collapsible-content-padding-inner: 0 0 1rem;
      --sf-collapsible-content-padding: 0 0.5rem;

      --sf-collapsible-scrollable-content-padding-inner: 0 1rem 1rem 0;
      --sf-collapsible-scrollable-content-padding: 0;
    }
  }

  .collapsible {
    display: flex;
    flex-direction: column;
    gap: var(--sf-collapsible-gap);
    border: var(--sf-collapsible-border);
    padding: var(--sf-collapsible-padding);
    width: 100%;

    @include media.query(lg) {
      border-bottom: var(--sf-collapsible-border-bottom);
    }

    &__header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--sf-collapsible-header-padding);

      @include media.query(lg) {
        position: relative;
        cursor: pointer;
      }
    }

    &__header {
      font-weight: var(--sf-collapsible-header-font-weight);
    }

    &__icon {
      display: none;
      z-index: 2;
      width: var(--sf-collapsible-icon-width-lg);
      height: var(--sf-collapsible-icon-height-lg);
      padding: var(--sf-collapsible-icon-padding-lg);
      transition: transform 0.3s ease;

      &--open {
        transform: rotate(180deg);
      }

      @include media.query(lg) {
        display: block;
      }
    }

    &__content {
      padding: var(--sf-collapsible-content-padding);
      transition: max-height 0.6s ease;

      &__inner {
        padding: var(--sf-collapsible-content-padding-inner);
      }

      @include media.query(lg) {
        max-height: 0;
        overflow: hidden;

        &--open {
          max-height: 80svh;
          overflow-y: auto;
        }
      }
    }

    &--scrollable .collapsible__content {
      padding: var(--sf-collapsible-scrollable-content-padding);
      overflow-y: hidden;

      .collapsible__content__inner {
        max-height: 300px;
        overflow-y: auto;
        padding: var(--sf-collapsible-scrollable-content-padding-inner);

        //Firefox
        scrollbar-width: auto;
        scrollbar-color: var(--primary-color, #000) var(--color-light-background, #d7d7d7);;

        &::-webkit-scrollbar {
          width: 0.375rem;
        }

        &::-webkit-scrollbar-track {
          background: var(--color-light-background, #d7d7d7);
          border-radius: var(--border-radius, 0);
        }

        &::-webkit-scrollbar-thumb {
          background: var(--primary-color, #000);
          border-radius: var(--border-radius, 0);
        }

        &::-webkit-scrollbar-thumb:hover {
          background: var(--primary-color-hover, #000);
        }
      }
    }

    &--enable {
      border-bottom: var(--sf-collapsible-border-bottom);

      .collapsible__header-container {
        position: relative;
        cursor: pointer;
        transition: transform 0.3s ease;
        padding: 0.5rem 0;
      }

      .collapsible__icon {
        display: block;
      }

      .collapsible__content {
        max-height: 0;
        overflow: hidden;

        &--open {
          max-height: 80svh;
          overflow-y: auto;
        }
      }
    }
  }
</style>
